<template>
  <div class="completion-step">
    <div class="completion-content">
      <!-- Main content block -->
      <div class="main-content-block">
        <!-- Image -->
        <div class="image-container">
          <img :src="avatarImage" alt="AI Assistant" class="completion-image" />
        </div>

        <!-- Categories -->
        <div class="categories-section">
          <h1 class="step-title">{{ step.title }}</h1>
          <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
        </div>
      </div>

      <!-- Bottom block -->
      <div class="bottom-block">
        <custom-button
          @click="handleViewCapsule"
          class="view-capsule-button"
          rounded
          label="Посмотреть образ"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { onMounted, ref } from 'vue'
import { useLookModal } from '@/features/look/store'
import type { LookStep } from '@/features/look/types/look-config'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'

interface Props {
  step: LookStep & { type: 'completion' }
}

const props = defineProps<Props>()
const lookStore = useLookModal()
const router = useRouter()

const avatarImage = '/img/survey-avatar.png'
const isSubmitted = ref(false)

async function handleViewCapsule() {
  if (!isSubmitted.value) {
    // Если опрос еще не отправлен, отправляем его
    if (!lookStore.lookModal.capsuleId) {
      try {
        await lookStore.submitLookSurvey()
        isSubmitted.value = true
      } catch (error) {
        console.error('Error submitting look survey:', error)
        return
      }
    } else {
      isSubmitted.value = true
    }
  }

  // Переход на страницу capsule
  const capsuleId = lookStore.lookModal.capsuleId
  if (capsuleId) {
    router.push(`/capsule/${capsuleId}`)
    lookStore.hideLookModal()
  }
}

onMounted(async () => {
  // Если опрос еще не отправлен, отправляем его
  if (!lookStore.lookModal.capsuleId) {
    try {
      await lookStore.submitLookSurvey()
      isSubmitted.value = true
    } catch (error) {
      console.error('Error submitting look survey:', error)
    }
  } else {
    isSubmitted.value = true
  }

  // Автоматический редирект на страницу capsule после небольшой задержки (fallback)
  const capsuleId = lookStore.lookModal.capsuleId
  if (capsuleId) {
    setTimeout(() => {
      if (!isSubmitted.value) {
        router.push(`/capsule/${capsuleId}`)
        lookStore.hideLookModal()
      }
    }, 2000)
  }
})
</script>

<style scoped>
.completion-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  height: 100%;
}

.completion-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 500px;
  height: 100%;
  width: 100%;
}

.main-content-block {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.image-container {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.completion-image {
  width: 330px;
  height: 330px;
  border-radius: 50%;
  object-fit: cover;
}

.categories-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.step-title {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
  line-height: 1.2;
  text-align: left;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  text-align: left;
}

.bottom-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 20px;
}

.view-capsule-button {
  width: 100%;
}

@media (min-width: 1919px) {
  .completion-content {
    padding: 40px 120px;
  }
}

@media (min-width: 1024px) {
  .completion-step {
    padding: 0;
  }

  .completion-content {
    padding: 40px;
    gap: 40px;
    max-width: 100%;
    justify-content: flex-start;
  }

  .main-content-block {
    flex-direction: row;
    align-items: center;
    gap: 40px;
    width: 100%;
  }

  .image-container {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .completion-image {
    width: 234px;
    height: 234px;
    border-radius: 20px;
    object-fit: cover;
  }

  .categories-section {
    gap: 8px;
    flex: 1;
  }

  .step-title {
    font-family: 'Gilroy', sans-serif;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.2;
    color: #232F45;
    letter-spacing: -0.04em;
  }

  .step-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
    letter-spacing: -0.04em;
  }

  .bottom-block {
    align-items: flex-end;
    padding: 20px 0 0 0;
  }

  .view-capsule-button {
    width: auto;
  }
}

@media (max-width: 1023px) {
  .completion-step {
    padding: 0;
  }

  .completion-content {
    padding: 20px 32px;
    gap: 40px;
    max-width: 100%;
  }

  .image-container {
    margin-bottom: 0;
    width: 100%;
  }

  .completion-image {
    width: 100%;
    height: 329px;
    border-radius: 20px;
    object-fit: cover;
  }

  .categories-section {
    gap: 20px;
  }

  .step-title {
    font-family: 'Gilroy', sans-serif;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.09;
    color: #232F45;
    letter-spacing: -0.02em;
  }

  .step-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
    letter-spacing: -0.06em;
  }
}

@media (max-width: 480px) {
  .completion-content {
    padding: 20px;
  }
}
</style>

